import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Zap, Send } from "lucide-react";

const AgentActionsCenter = ({ userId, onRefresh }) => {
  const [command, setCommand] = useState("");
  const [processing, setProcessing] = useState(false);
  const [recentCommands, setRecentCommands] = useState([]);

  const handleCommand = async () => {
    if (!command.trim()) {
      toast.error("Please enter a command");
      return;
    }

    setProcessing(true);
    try {
      // Process natural language command
      const response = await axios.post(`${API}/agent/command/${userId}`, null, {
        params: { command: command }
      });
      
      console.log("Command response:", response.data);
      
      setRecentCommands(prev => [
        { text: command, timestamp: new Date(), result: response.data.mode },
        ...prev.slice(0, 4)
      ]);
      
      // Show success message with deal info if available
      if (response.data.dealClosed && response.data.dealValue) {
        toast.success(`Command executed: ${response.data.mode} mode. Deal closed: $${(response.data.dealValue / 1000).toFixed(0)}K`);
      } else {
        toast.success(`Command executed: ${response.data.mode} mode activated`);
      }
      
      setCommand("");
      
      // Add a small delay to ensure database has persisted the changes
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh dashboard data
      await onRefresh();
    } catch (error) {
      console.error("Error executing command:", error);
      toast.error("Failed to execute command");
    } finally {
      setProcessing(false);
    }
  };

  const handleQuickAction = async (action) => {
    setProcessing(true);
    try {
      let response;
      if (action === 'Hunt') {
        response = await axios.post(`${API}/agent/hunt/${userId}`, null, {
          params: { target_count: 10 }
        });
      } else if (action === 'Farm') {
        response = await axios.post(`${API}/agent/farm/${userId}`);
      } else if (action === 'Advisor') {
        response = await axios.post(`${API}/agent/advisor/${userId}`);
      }
      
      console.log(`${action} mode response:`, response.data);
      
      // Show success message with deal info if available
      if (response.data.dealClosed && response.data.dealValue) {
        toast.success(`${action} mode completed! Deal closed: $${(response.data.dealValue / 1000).toFixed(0)}K`);
      } else {
        toast.success(`${action} mode completed successfully`);
      }
      
      setRecentCommands(prev => [
        { text: `${action} Mode`, timestamp: new Date(), result: action.toLowerCase() },
        ...prev.slice(0, 4)
      ]);
      
      // Add a small delay to ensure database has persisted the changes
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh dashboard data
      await onRefresh();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Action failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card className="bg-[#0f1419] border-gray-800/50 p-6 h-[600px] flex flex-col" data-testid="agent-actions-center">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
          <Zap className="h-5 w-5 text-[#3dd9c5]" />
          Agent Actions
        </h2>
        <p className="text-sm text-gray-400">Self-Directed Execution</p>
      </div>

      {/* Command Input */}
      <div className="mb-6">
        <div className="flex gap-2">
          <Input
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCommand()}
            placeholder="Enter a command"
            className="bg-white/5 border-[#3dd9c5]/50 text-white placeholder:text-gray-500 focus:border-[#3dd9c5]"
            data-testid="command-input"
            disabled={processing}
          />
          <Button
            onClick={handleCommand}
            disabled={processing}
            className="bg-[#3dd9c5] hover:bg-[#35c4b5] text-white px-6"
            data-testid="send-command-btn"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Quick Action Buttons */}
      <div className="flex gap-3 mb-6">
        <Button
          onClick={() => handleQuickAction('Hunt')}
          disabled={processing}
          className="bg-[#e91e8c] hover:bg-[#d01a7d] text-white rounded-full px-6 py-2"
          data-testid="hunt-btn"
        >
          Hunt
        </Button>
        <Button
          onClick={() => handleQuickAction('Farm')}
          disabled={processing}
          className="bg-[#e91e8c] hover:bg-[#d01a7d] text-white rounded-full px-6 py-2"
          data-testid="farm-btn"
        >
          Farm
        </Button>
        <Button
          onClick={() => handleQuickAction('Advisor')}
          disabled={processing}
          className="bg-[#e91e8c] hover:bg-[#d01a7d] text-white rounded-full px-6 py-2"
          data-testid="advisor-btn"
        >
          Advisor
        </Button>
      </div>

      {/* Recent Commands */}
      <div className="flex-1 overflow-hidden">
        <h3 className="text-sm font-semibold text-gray-400 mb-3">Recent Commands</h3>
        <ScrollArea className="h-[300px] pr-4">
          {recentCommands.length > 0 ? (
            <div className="space-y-3">
              {recentCommands.map((cmd, idx) => (
                <div
                  key={idx}
                  className="bg-white/5 backdrop-blur-sm border border-gray-700/50 rounded-lg p-3"
                  data-testid={`recent-command-${idx}`}
                >
                  <p className="text-sm text-white mb-1">{cmd.text}</p>
                  <p className="text-xs text-gray-500">
                    {cmd.timestamp.toLocaleTimeString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Zap className="h-12 w-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No recent commands</p>
              <p className="text-gray-600 text-xs mt-1">Use quick actions or enter a command above</p>
            </div>
          )}
        </ScrollArea>
      </div>
    </Card>
  );
};

export default AgentActionsCenter;
